/**
 * Current UTC offset for an IANA zone, e.g. "UTC+10", "UTC-4", "UTC+05:30".
 * Reflects DST for the given instant (defaults to now).
 */

type GmtOffset = {
  sign: 1 | -1;
  hours: number;
  minutes: number;
};

function parseGmtOffset(timeZone: string, at: Date): GmtOffset | null {
  if (timeZone === 'UTC') return { sign: 1, hours: 0, minutes: 0 };

  const offsetName = new Intl.DateTimeFormat('en-US', {
    timeZone,
    timeZoneName: 'shortOffset',
  })
    .formatToParts(at)
    .find((part) => part.type === 'timeZoneName')?.value;

  const match = offsetName?.match(/GMT([+-])(\d{1,2})(?::(\d{2}))?/i);
  if (!match?.[1] || match[2] == null) return null;

  return {
    sign: match[1] === '-' ? -1 : 1,
    hours: Number(match[2]),
    minutes: match[3] != null ? Number(match[3]) : 0,
  };
}

/** Signed offset in hours (Adelaide +9.5). 0 when the zone cannot be parsed. */
export function utcOffsetHours(timeZone: string, at: Date = new Date()): number {
  const parsed = parseGmtOffset(timeZone, at);
  if (!parsed) return 0;
  return parsed.sign * (parsed.hours + parsed.minutes / 60);
}

export function formatUtcOffsetLabel(timeZone: string, at: Date = new Date()): string {
  if (timeZone === 'UTC') return 'UTC';

  const parsed = parseGmtOffset(timeZone, at);
  if (!parsed) return 'UTC';

  const { sign, hours, minutes } = parsed;
  const signChar = sign < 0 ? '-' : '+';
  if (hours === 0 && minutes === 0) return 'UTC+0';
  if (minutes === 0) return `UTC${signChar}${hours}`;
  return `UTC${signChar}${String(hours).padStart(2, '0')}:${String(minutes).padStart(2, '0')}`;
}

/** e.g. "AU - Melbourne (UTC+10)". UTC stays "UTC". */
export function withUtcOffsetInLabel(baseLabel: string, timeZone: string, at: Date = new Date()): string {
  if (timeZone === 'UTC') return 'UTC';
  return `${baseLabel} (${formatUtcOffsetLabel(timeZone, at)})`;
}
